// Added by Sejune Oh on 2019-03-08
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { UserAccessLevelProps, ElementTimeStampProps } from '@/helper/define'
import Intl from '@/helper/Intl'
import { getQueryPath } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root store
import Rootstore from '../../index'

// Store
import {
  reqGetUser,
  reqGetListUsers,
  reqCreateUser,
  reqDeleteUser,
  reqUpdateUser,
  reqChangeUserPassword,
  reqResetUserPassword,
  reqAssignGroupsToUser,
  reqGetAssignedGroups,
  reqResetAuth2Data,
} from './UserStore.service'
import { GroupElementProps } from '../group'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface UserElementProps extends ElementTimeStampProps {
  id?: string
  username?: string
  display_name?: string
  email?: string
  ui_language?: string
  password?: string
  description?: string
  access_level?: number
  expired_at?: string
  activated?: boolean
  noti_enabled?: boolean
  change_password_at_next_login?: boolean
  last_connected_at?: string
  user_custom_infos?: UserCustomInfoProp[]
  auth2_enabled?: boolean
  auth2_data?: string
  auth2_type?: string
}

export interface DisplableUserElementProps extends UserElementProps {
  assgiend_fk_group_ids?: string[]
  is_activated?: string
  str_access_level?: string
  current_pwd?: string
  confirm_pwd?: string
  disp_label?: string
  permission?: string[]
  external?: boolean
}

export interface UserCustomInfoProp {
  id?: string
  key: string
  value: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class UserStore {
  currentUser?: UserElementProps
  _elements: Array<UserElementProps | DisplableUserElementProps> = []
  totalCount: number = 0
  root?: Rootstore
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: Rootstore) {
    this.root = root
    makeAutoObservable(this)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  initStore = async (currentUserId: string) => {
    this.currentUser = await this.getElement(currentUserId)
  }

  getElement = async (id: string) => {
    const { data: rtnElement } = await reqGetUser(id)
    return rtnElement
  }

  getElements = async (path: string) => {
    const {
      data: { total_count, items },
    } = await reqGetListUsers(path)
    this._setTotalCount(parseInt(total_count!))
    return items!
  }

  addElement = async (element: UserElementProps) => {
    const { data: rtnElement } = await reqCreateUser(element)
    return rtnElement
  }

  removeElement = async (id: string) => {
    return await reqDeleteUser(id)
  }

  updateElement = async (id: string, element: UserElementProps) => {
    const { data: updatedElement } = await reqUpdateUser(id, element)

    if (Number(id) === Number(this.currentUser?.id)) {
      this.currentUser = updatedElement
    }
    return updatedElement.id
  }

  resetAuth2Data = async (id: string) => {
    const { data: isResetAuth2 } = await reqResetAuth2Data(id)
    return isResetAuth2
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  checkDuplicatedUserName = async (userName: string) => {
    const filter = { $op: 'and', username: userName }
    const path = getQueryPath({ pageIndex: 0, orderBy: 'username', filter: JSON.stringify(filter) })

    const elements = await this.getElements(path)
    return elements.length > 0
  }

  checkDuplicatedEmail = async (email: string) => {
    const filter = { $op: 'and', email }
    const path = getQueryPath({ pageIndex: 0, orderBy: 'email', filter: JSON.stringify(filter) })

    const elements = await this.getElements(path)
    return elements.length > 0
  }

  getAssignedGroups = async (id: string) => {
    const {
      data: { items },
    } = await reqGetAssignedGroups(id)
    return items as Array<GroupElementProps>
  }

  updateAssignedGroups = async (id: string, groupIds: Array<string>) => {
    return await reqAssignGroupsToUser(id, groupIds)
  }

  resetPassword = async (id: string) => {
    const {
      data: { password },
    } = await reqResetUserPassword(id)
    return password
  }

  updatePassword = async ({ current_pwd, password, id }: DisplableUserElementProps) => {
    return await reqChangeUserPassword(id!, {
      id: `${id}`,
      new_password: password!,
      cur_password: current_pwd!,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setElements = (elements: Array<UserElementProps>) => {
    this._elements = elements
  }

  setStrActivated = (elements: Array<UserElementProps>, accessLevelList: Array<UserAccessLevelProps>) => {
    this._elements = elements.map((userElement) => {
      const access_level = accessLevelList.find(({ enumValue }) => userElement!.access_level === enumValue)
      let str_access_level = ''

      if (access_level) {
        str_access_level = access_level.label
      } else {
        const index = accessLevelList.findIndex(({ enumValue }) => userElement!.access_level! < enumValue)
        str_access_level = accessLevelList[!!index && index > 0 ? index - 1 : 0].label
      }

      return {
        ...userElement,
        is_activated: userElement!.activated
          ? Intl.getIntlText({ id: 'htmljs_react.global.userstore.text_enabled' })
          : Intl.getIntlText({ id: 'htmljs_react.global.userstore.text_disabled' }),
        str_access_level,
      } as DisplableUserElementProps
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get elements() {
    return this._elements
  }
}

